import React, { SyntheticEvent } from "react";

// material ui
import { Chip, Box, Autocomplete, TextField, Typography, ListItem } from "@mui/material";

type AutocompleteProps = {
  title: string;
  label: string;
  value?: any[];
  data: any[];
  groupByField?: string;
  renderOptionField: string;
  renderTagField: string;
  showTitle: boolean;
  showLabel: boolean;
  fixedOptions?: any[];
  noOptionsText?: string;
  condensed?: boolean;
  limitTags?: number;
  filterSelectedOptions?: boolean;
  changeHandler: (e: SyntheticEvent<Element, Event>, v: any) => void;
  filterHandler: (options: any, params: any) => any[];
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  noWrap?: boolean;
};

// ==============================|| AUTOCOMPLETE - CREATABLE ||============================== //

export default function CreatableAutocomplete({
  title,
  showTitle,
  label,
  showLabel,
  data,
  value,
  groupByField,
  renderOptionField,
  renderTagField,
  fixedOptions,
  limitTags,
  filterSelectedOptions,
  changeHandler,
  filterHandler,
  multiple,
  disabled,
  className,
  noWrap
}: AutocompleteProps) {
  // Setup parameters
  const fixedOptionsIn = fixedOptions ? fixedOptions : [];
  const valueTest = filterSelectedOptions ? value?.map((el) => el[renderOptionField]) : null;

  return (
    <>
      {showTitle && (
        <Typography variant="h5" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}

      <div className={className}>
        <Autocomplete
          multiple={multiple}
          disabled={disabled}
          groupBy={groupByField ? (option) => option[groupByField] : undefined}
          fullWidth
          freeSolo
          limitTags={limitTags ? limitTags : -1}
          value={value}
          onChange={changeHandler}
          filterOptions={filterHandler}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          id="auto-complete-creatable"
          options={data}
          ListboxProps={{
            sx: {
              "& .MuiAutocomplete-groupLabel": {
                lineHeight: (theme) => theme.spacing(3),
              },
            },
          }}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          getOptionDisabled={(option) => {
            if (option.disabled !== undefined) return option.disabled;
            return false;
          }}
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === "string") {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return option.inputValue;
            }
            // Regular option
            return option.label;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={label}
              label={showLabel ? title : null}
              sx={{ "& .MuiFormLabel-root": { fontSize: 14 } }}
              color="secondary"
            />
          )}
          renderOption={(props, option: any) => {
            if (valueTest && valueTest.includes(option[renderOptionField])) return null;

            const { key, ...optionProps } = props;
            return (
              <ListItem
                key={key}
                {...optionProps}
                sx={{
                  "&&:hover": {
                    backgroundColor: "primary.lighter",
                  },
                }}
              >
                {option[renderOptionField]}
              </ListItem>
            );
          }}
          ListboxComponent={React.forwardRef((props, ref) => (
            <Box {...props} ref={ref} />
          ))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option[renderTagField]}
                  {...tagProps}
                  disabled={disabled || fixedOptionsIn.indexOf(option) !== -1}
                />
              );
            })
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              p: 0.4,
              height: multiple ? null : 41,
              "&:hover, &:focus-within": {
                flexWrap: (noWrap || disabled) ? "nowrap" : "wrap", // Allow wrapping when hovered or focused
              },
              flexWrap: (noWrap || disabled) ? "nowrap" : "initial", // Prevent wrapping by default
            },
            "& .MuiFormLabel-root": {
              marginTop: "-5px",
            },
            "& .MuiInputLabel-shrink": {
              marginTop: "0px",
            },
            "& .MuiAutocomplete-tag": {
              bgcolor: "primary.lighter",
              borderRadius: 1,
              height: 28,
              pl: 1.5,
              pr: 1.5,
              lineHeight: "28px",
              "& .MuiChip-label": {
                paddingLeft: 0,
                paddingRight: 0,
              },
              "& .MuiSvgIcon-root": {
                color: "primary.main",
                ml: 1,
                mr: -0.75,
                "&:hover": {
                  color: "primary.main",
                },
              },
            },
            "& .MuiAutocomplete-inputRoot": {
              overflow: "hidden",
            }
          }}
        />
      </div>
    </>
  );
}
